import { useContext, useTransition } from 'react';
import type { Selection } from 'react-stately';
import { OverlayTriggerStateContext } from '@launchpad-ui/components';

import type {
  MaintainerComboBoxItem,
  MemberItem,
  MultipleMaintainerProps,
  SingleMaintainerProps,
  TeamItem,
} from '../types';

type MaintainerSelectionParams = (SingleMaintainerProps | MultipleMaintainerProps) & {
  itemsInSections: MaintainerComboBoxItem[];
  closeOnSelection?: boolean;
};

export function useMaintainerSelection(props: MaintainerSelectionParams) {
  const { selectionMode, closeOnSelection, itemsInSections } = props;
  const overlayState = useContext(OverlayTriggerStateContext);
  const [isPending, startTransition] = useTransition();

  // Extract the appropriate IDs based on selectionMode
  const maintainerId = selectionMode === 'single' ? props.maintainerId : undefined;
  const maintainerTeamKey = selectionMode === 'single' ? props.maintainerTeamKey : undefined;

  // Helper function to find an item across all sections
  const findItemById = (id: string): MemberItem | TeamItem | undefined => {
    for (const section of itemsInSections) {
      const foundItem = section.children.find((item) => item.id === id);
      if (foundItem) {
        return foundItem;
      }
    }
    return undefined;
  };

  const handleSingleSelectionChange = (selection: Selection, singleSelectionProps: SingleMaintainerProps) => {
    const { onMemberSelectionChange, onTeamSelectionChange, onSelectionChange } = singleSelectionProps;

    if (typeof selection === 'string') {
      return;
    }

    if (selection.size === 0) {
      if (maintainerId !== undefined && onMemberSelectionChange !== undefined) {
        return startTransition(() => {
          onMemberSelectionChange(undefined, undefined);
        });
      }

      if (maintainerTeamKey !== undefined && onTeamSelectionChange !== undefined) {
        return startTransition(() => {
          onTeamSelectionChange(undefined, undefined);
        });
      }
      if ((maintainerId !== undefined || maintainerTeamKey !== undefined) && onSelectionChange !== undefined) {
        return startTransition(() => {
          onSelectionChange(undefined);
        });
      }
      return;
    }

    const [key] = Array.from(selection);
    const item = findItemById(String(key));

    if (!item) {
      return;
    }

    if (item.type === 'member' && onMemberSelectionChange !== undefined) {
      startTransition(() => {
        onMemberSelectionChange(item.id, item.resource);
      });
    } else if (item.type === 'team' && onTeamSelectionChange !== undefined) {
      startTransition(() => {
        onTeamSelectionChange(item.id, item.resource);
      });
    } else if (onSelectionChange !== undefined) {
      startTransition(() => {
        onSelectionChange(item);
      });
    }

    if (closeOnSelection && overlayState) {
      overlayState.close();
    }
  };

  const handleMultipleSelectionChange = (selection: Selection, multipleSelectionProps: MultipleMaintainerProps) => {
    const { onMemberSelectionChange, onTeamSelectionChange, onSelectionChange } = multipleSelectionProps;

    const selectedKeys = Array.from(selection);
    // Keys already known to be members/teams, so id-based callbacks preserve selections
    // that haven't resolved into `itemsInSections` yet (e.g. pasted or freshly invited ids).
    const knownTeamKeys = new Set(multipleSelectionProps.maintainerTeamKeys ?? []);
    const memberIds: string[] = [];
    const teamKeys: string[] = [];
    const resolvedMembers: MemberItem[] = [];
    const resolvedTeams: TeamItem[] = [];

    for (const key of selectedKeys) {
      const id = String(key);
      const item = findItemById(id);
      if (item?.type === 'member') {
        memberIds.push(item.id);
        resolvedMembers.push(item);
      } else if (item?.type === 'team') {
        teamKeys.push(item.id);
        resolvedTeams.push(item);
      } else if (knownTeamKeys.has(id)) {
        teamKeys.push(id);
      } else {
        memberIds.push(id);
      }
    }

    if (onMemberSelectionChange !== undefined) {
      startTransition(() => {
        onMemberSelectionChange(
          memberIds,
          resolvedMembers.map((m) => m.resource),
        );
      });
    }

    if (onTeamSelectionChange !== undefined) {
      startTransition(() => {
        onTeamSelectionChange(
          teamKeys,
          resolvedTeams.map((t) => t.resource),
        );
      });
    }
    if (onSelectionChange !== undefined) {
      // memberIds/teamKeys, unlike resolvedMembers/resolvedTeams, retain ids filtered out of the option list.
      const memberResourceById = new Map(resolvedMembers.map((m) => [m.id, m.resource] as const));
      const teamResourceById = new Map(resolvedTeams.map((t) => [t.id, t.resource] as const));
      const members = memberIds.map((id) => ({ id, type: 'member' as const, resource: memberResourceById.get(id) }));
      const teams = teamKeys.map((id) => ({ id, type: 'team' as const, resource: teamResourceById.get(id) }));

      startTransition(() => {
        onSelectionChange([...members, ...teams]);
      });
    }
  };

  const handleSelectionChange = (selection: Selection) => {
    if (selection === 'all') {
      return;
    }

    if (selectionMode === 'single') {
      handleSingleSelectionChange(selection, props);
    } else {
      handleMultipleSelectionChange(selection, props);
    }
  };

  return { handleSelectionChange, isPending };
}
